<template>
  <div class="border rounded-lg bg-white p-4">
    <div class="flex flex-wrap items-center justify-between gap-2 mb-3">
      <div class="text-xs text-gray-600">
        {{ title }} -
        <template v-if="effectiveRowLimit === 0">
          showing all {{ rows.length }}
        </template>
        <template v-else>
          showing top {{ Math.min(effectiveRowLimit, rows.length) }} of {{ rows.length }}
        </template>
      </div>
      <div class="flex flex-wrap items-center justify-end gap-2">
        <template v-if="showRowLimit">
          <label class="text-xs text-gray-600">Rows</label>
          <select :value="effectiveRowLimit" class="border rounded px-2 py-1 text-xs" @change="setRowLimit(Number($event.target.value))">
            <option v-for="opt in normalizedRowLimitOptions" :key="`row-limit-${opt}`" :value="opt">
              {{ opt === 0 ? 'All' : `Top ${opt}` }}
            </option>
          </select>
        </template>
        <template v-if="showOrientation">
          <button
            class="border rounded px-2 py-1 text-xs hover:bg-gray-50"
            type="button"
            @click="toggleOrientation"
          >
            Orientation: {{ effectiveOrientation === 'horizontal' ? 'Horizontal' : 'Vertical' }}
          </button>
        </template>
        <template v-if="effectiveOrientation === 'vertical' && displayRows.length > 0">
          <label class="text-xs text-gray-600">Bars</label>
          <select :value="verticalPageSize" class="border rounded px-2 py-1 text-xs" @change="setVerticalPageSize(Number($event.target.value))">
            <option :value="8">8</option>
            <option :value="12">12</option>
            <option :value="16">16</option>
          </select>
          <button class="border rounded px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-50" type="button" :disabled="verticalPage <= 0" @click="prevVerticalPage">Prev</button>
          <button class="border rounded px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-50" type="button" :disabled="verticalPage >= verticalTotalPages - 1" @click="nextVerticalPage">Next</button>
        </template>
      </div>
    </div>

    <div v-if="!displayRows.length" class="text-xs text-gray-500">No rows to display.</div>
    <div v-else-if="effectiveOrientation === 'horizontal'" class="space-y-2 overflow-auto pr-1" :style="{ maxHeight: `${maxHeightPx}px` }">
      <div v-for="(row, idx) in displayRows" :key="rowKey(row, idx)">
        <div class="flex justify-between text-xs mb-1">
          <span class="truncate max-w-[70%]" :title="rowLabel(row, idx)">{{ rowLabel(row, idx) }}</span>
          <span>
            <slot name="value" :row="row">{{ formattedValue(row) }}</slot>
          </span>
        </div>
        <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <div :class="barClass" :style="{ width: barWidth(row) }"></div>
        </div>
      </div>
    </div>
    <div v-else class="overflow-visible pb-2">
      <div class="grid gap-3 items-end pl-1 pr-1" :style="verticalGridStyle">
        <div v-for="(row, idx) in verticalRows" :key="rowKey(row, idx)" class="min-w-0">
          <div class="h-36 flex items-end justify-center">
            <div :class="verticalBarClass" :style="{ height: `${barHeight(row)}px` }"></div>
          </div>
          <div class="text-[10px] leading-tight text-gray-700 text-center mt-1 whitespace-normal break-words min-h-[2rem]" :title="rowLabel(row, idx)">
            {{ rowLabel(row, idx) }}
          </div>
          <div class="text-[10px] text-gray-500 text-center mt-1">
            <slot name="value" :row="row">{{ formattedValue(row) }}</slot>
          </div>
        </div>
      </div>
      <div v-if="displayRows.length > verticalPageSize" class="text-[11px] text-gray-500 text-right mt-2 pr-1">
        {{ verticalRangeLabel }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BarComparisonChart',
  props: {
    title: {
      type: String,
      default: 'Visual comparison',
    },
    rows: {
      type: Array,
      default: () => [],
    },
    labelFn: {
      type: Function,
      default: null,
    },
    valueFn: {
      type: Function,
      default: null,
    },
    keyFn: {
      type: Function,
      default: null,
    },
    valueSuffix: {
      type: String,
      default: '',
    },
    rowLimit: {
      type: Number,
      default: null,
    },
    defaultRowLimit: {
      type: Number,
      default: 15,
    },
    rowLimitOptions: {
      type: Array,
      default: () => [15, 30, 50, 0],
    },
    orientation: {
      type: String,
      default: null,
    },
    defaultOrientation: {
      type: String,
      default: 'horizontal',
    },
    showRowLimit: {
      type: Boolean,
      default: true,
    },
    showOrientation: {
      type: Boolean,
      default: true,
    },
    maxHeightPx: {
      type: Number,
      default: 320,
    },
    barStyle: {
      type: String,
      default: 'gradient',
    },
    maxValueOverride: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      internalRowLimit: Number(this.defaultRowLimit || 0),
      internalOrientation: this.defaultOrientation === 'vertical' ? 'vertical' : 'horizontal',
      verticalPage: 0,
      verticalPageSize: 12,
    }
  },
  computed: {
    normalizedRowLimitOptions() {
      const opts = Array.isArray(this.rowLimitOptions) ? this.rowLimitOptions : []
      const normalized = opts
        .map(v => Number(v))
        .filter(v => Number.isFinite(v) && v >= 0)
      const unique = Array.from(new Set(normalized))
      if (!unique.length) return [15, 30, 50, 0]
      return unique
    },
    effectiveRowLimit() {
      if (this.rowLimit !== null && this.rowLimit !== undefined) {
        const v = Number(this.rowLimit)
        return Number.isFinite(v) && v >= 0 ? v : 0
      }
      const v = Number(this.internalRowLimit)
      return Number.isFinite(v) && v >= 0 ? v : 0
    },
    effectiveOrientation() {
      const raw = this.orientation !== null && this.orientation !== undefined
        ? this.orientation
        : this.internalOrientation
      return raw === 'vertical' ? 'vertical' : 'horizontal'
    },
    displayRows() {
      const rows = Array.isArray(this.rows) ? this.rows : []
      if (this.effectiveRowLimit === 0) return rows
      return rows.slice(0, this.effectiveRowLimit)
    },
    verticalTotalPages() {
      const size = Math.max(1, Number(this.verticalPageSize || 1))
      return Math.max(1, Math.ceil(this.displayRows.length / size))
    },
    verticalRows() {
      const size = Math.max(1, Number(this.verticalPageSize || 1))
      const page = Math.max(0, Math.min(this.verticalPage, this.verticalTotalPages - 1))
      const start = page * size
      return this.displayRows.slice(start, start + size)
    },
    verticalGridStyle() {
      const cols = Math.max(this.verticalRows.length, 1)
      return {
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
      }
    },
    verticalRangeLabel() {
      const size = Math.max(1, Number(this.verticalPageSize || 1))
      const page = Math.max(0, Math.min(this.verticalPage, this.verticalTotalPages - 1))
      const start = page * size + 1
      const end = Math.min((page + 1) * size, this.displayRows.length)
      return `${start}-${end} of ${this.displayRows.length}`
    },
    maxValue() {
      if (this.maxValueOverride !== null && this.maxValueOverride !== undefined) {
        const forced = Number(this.maxValueOverride)
        if (Number.isFinite(forced) && forced > 0) return forced
      }
      const vals = this.displayRows.map(row => this.numericValue(row))
      const max = vals.length ? Math.max(...vals) : 0
      return Number.isFinite(max) && max > 0 ? max : 0
    },
    barClass() {
      if (this.barStyle === 'solid') {
        return 'h-2 rounded-full bg-black'
      }
      return 'h-2 rounded-full bg-gradient-to-r from-black to-gray-600'
    },
    verticalBarClass() {
      if (this.barStyle === 'solid') {
        return 'w-6 rounded-t bg-black'
      }
      return 'w-6 rounded-t bg-gradient-to-t from-black to-gray-600'
    },
  },
  methods: {
    setRowLimit(limit) {
      const resolved = Number.isFinite(limit) && limit >= 0 ? limit : 0
      if (this.rowLimit !== null && this.rowLimit !== undefined) {
        this.$emit('update:rowLimit', resolved)
        return
      }
      this.internalRowLimit = resolved
    },
    setOrientation(value) {
      const resolved = value === 'vertical' ? 'vertical' : 'horizontal'
      if (this.orientation !== null && this.orientation !== undefined) {
        this.$emit('update:orientation', resolved)
        return
      }
      this.internalOrientation = resolved
    },
    toggleOrientation() {
      this.setOrientation(this.effectiveOrientation === 'horizontal' ? 'vertical' : 'horizontal')
    },
    setVerticalPageSize(value) {
      const resolved = [8, 12, 16].includes(value) ? value : 12
      this.verticalPageSize = resolved
      this.verticalPage = 0
    },
    prevVerticalPage() {
      this.verticalPage = Math.max(0, this.verticalPage - 1)
    },
    nextVerticalPage() {
      this.verticalPage = Math.min(this.verticalTotalPages - 1, this.verticalPage + 1)
    },
    rowKey(row, index) {
      if (this.keyFn) return String(this.keyFn(row, index))
      return String(row?.compare_key || row?.ceremony_id || index)
    },
    rowLabel(row, index) {
      if (this.labelFn) return String(this.labelFn(row, index))
      return this.rowKey(row, index)
    },
    numericValue(row) {
      const raw = this.valueFn ? this.valueFn(row) : 0
      const value = Number(raw)
      if (!Number.isFinite(value)) return 0
      return Math.max(0, value)
    },
    barWidth(row) {
      if (this.maxValue <= 0) return '0%'
      const pct = (this.numericValue(row) / this.maxValue) * 100
      return `${Math.max(0, Math.min(100, pct)).toFixed(2)}%`
    },
    barHeight(row) {
      if (this.maxValue <= 0) return 0
      const maxHeight = 140
      return Math.max(0, Math.min(maxHeight, Math.round((this.numericValue(row) / this.maxValue) * maxHeight)))
    },
    formattedValue(row) {
      const value = this.numericValue(row)
      return `${value}${this.valueSuffix}`
    },
  },
  watch: {
    displayRows() {
      if (this.verticalPage >= this.verticalTotalPages) {
        this.verticalPage = Math.max(0, this.verticalTotalPages - 1)
      }
    },
    effectiveOrientation() {
      this.verticalPage = 0
    },
  },
}
</script>
